import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, firestore } from '../../database/firebase';
import { useNavigate } from 'react-router-dom';
import { Building2, Lock, Mail } from 'lucide-react';

export default function TeamMemberLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [organization, setOrganization] = useState(null);
  const navigate = useNavigate();

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const teamMemberDoc = await getDoc(doc(firestore, 'teamMembers', user.uid));

      if (teamMemberDoc.exists()) {
        const teamData = teamMemberDoc.data();
        setOrganization(teamData);

        localStorage.setItem('teamMemberData', JSON.stringify(teamData));

        navigate('/team-dashboard');
      } else {
        setError('Access denied. You are not a registered team member.');
        await auth.signOut();
      }
    } catch (error) {
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        setError('Invalid email or password.');
      } else {
        setError('An error occurred during login. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-[92%] max-w-sm sm:w-full md:max-w-md"
      >
        <div className="rounded-2xl bg-white p-6 shadow-xl md:p-8">
          <div className="mb-8 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1 }}
              className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 md:h-16 md:w-16"
            >
              <Building2 className="h-8 w-8 text-slate-600" />
            </motion.div>
            <h1 className="text-xl font-bold text-slate-900 md:text-2xl">Team Member Access</h1>
            <p className="mt-2 text-sm text-slate-600 md:text-base">
              Sign in to access your organization's dashboard
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 py-3 pl-10 pr-4 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-slate-400" />
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 py-3 pl-10 pr-4 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                  placeholder="Enter your password"
                  required
                />
              </div>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-xl border border-red-200 bg-red-50 p-4"
              >
                <p className="text-sm text-red-600">{error}</p>
              </motion.div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-slate-900 py-2.5 font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 md:py-3"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="mr-2 h-5 w-5 rounded-full border-2 border-white border-t-transparent"
                  />
                  Signing in...
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Need help? Contact your organization administrator
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
